import { debounce, isEmpty, uniq } from 'lodash';
import {
  addDefaultMaterialToLabelApi,
  apiGetAllAspectRatio,
  getConfiguratorApi,
  getConfiguratorScenes,
  getDefaultTextures,
  getLabelOptions,
  getLabelsDataInPrototype,
  getOptionsDataInPrototype,
  getPublicProductData,
  getSceneViewsApi,
  getSelectedTextures,
  removeOptionsFromLabelOptimistic,
  updateConfiguratorData,
  updateOptionApi,
  updateViewTransformationOptimistic,
  uploadDefaultThumbnail,
} from '../../../api/configuratorApi';
import { PERMISSIONS } from '../../../constants/permissionConstants';
import {
  ASPECT_RATIO,
  CONFIGURATOR_TYPE,
  DEFAULT_END_ANGLE,
  DEFAULT_START_ANGLE,
  GLASS_MATERIAL_ID,
  OPTION_TYPE,
} from '../../../constants/productConfiguratorConstants';
import { AUTH_TOKEN, CURRENT_USER, REFRESH_TOKEN } from '../../../constants/projectConstants';
import { apiGetPreviewListing } from '../../../helpers/api';
import { base64ToBlob, getLocalStorage } from '../../../helpers/jsHelper';
import { dispatcher } from '../../../helpers/projectHelper';
import { errorToastV2, warningToast } from '../../../helpers/toastHelper';
import {
  configuratorActions,
  selectAddRenderersCount,
  selectAllAspectRatio,
  selectBackupLabels,
  selectConfiguratorData,
  selectConfiguratorId,
  selectConfiguratorVersion,
  selectDefaultTextures,
  selectInitialObjectProperties,
  selectIsAddViewRoute,
  selectIsViewSettingsRoute,
  selectIsViewsRoute,
  selectObjectsWithDefaultThumbnail,
  selectRenderPreview,
  selectSelectedSceneId,
  selectUnitySceneLoaded,
} from '../../../redux/slicers/configurator/configurator.slicer';
import { selectSortedLabelOptions } from '../../../redux/slicers/configurator/configuratorLabelDetails.slicer';
import { selectAllLinkedObjects } from '../../../redux/slicers/configurator/configuratorSceneLabels.slicer';
import { selectSelectedObjects } from '../../../redux/slicers/configurator/configuratorUnityObject.slicer';
import {
  selectCurrentSceneViews,
  selectSelectedView,
  selectSelectedViewInScene,
} from '../../../redux/slicers/configurator/configuratorViews.slicer';
import { getAppState } from '../../../redux/store';
import { forceApplyOption } from '../unity/objectPropertiesMethods';
import { LabelsController, configuratorBridge, configuratorModule } from '../unity/unityMethods';
import { CONFIGURATOR_UNITY_EVENT } from '../unity/unityCallbacks';
import { waitForEventOnce } from '../../../helpers/unityPubSub';
import {
  selectIsConfiguratorPropSelected,
  selectShowInfoWarningPopup,
} from '../../../redux/slicers/admin/curatorUnityObjectSlicer';
import { v4 as uuidv4 } from 'uuid';
import { NAME_EXCLUDED_SPECIAL_CHARS_V2_GLOBAL } from '../../../constants/validationConstants';
import { loadSceneLabels } from '../../../redux/slicers/configurator/configurator.actions';
import { init } from '../hooks/useInitConfigurator';
import { selectOpenAlignmentTool } from '../../../redux/slicers/admin/curatorSlicer';
import {
  LEISURE_CREATIONS_DOMAIN,
  TEST_DOMAIN,
  WESTMINSTER_DOMAIN,
} from '../../../helpers/posthog';
import { RETAIL_AUTOMATION_ROUTE_V2, RETAIL_CONFIGURATOR_VIEWER_ROUTE } from '../../../constants/routePathConstants';

export const getOptionTextLabel = (option) => {
  if (option.texture_type === OPTION_TYPE.MATERIAL) return 'Material';
  if (option.texture_type === OPTION_TYPE.TEXTURE) return 'Texture';
  if (option.texture_type === OPTION_TYPE.COLOR) return 'Color';

  return 'Unknown';
};

export const isConfiguratorView = () => {
  return window.location.pathname.includes('product-configurators');
};

export const isPublicPageView = () => {
  return (
    window.location.pathname.includes('configurator-viewer') ||
    window.location.pathname.includes('configurator-public-viewer') ||
    window.location.pathname.includes('/ar-viewer') ||
    window.location.pathname.includes('/p-ar-viewer') ||
    window.location.pathname.includes('/product/public/360-viewer') ||
    window.location.pathname.includes('/product/public/360-viewer/v2') ||
    window.location.pathname.includes('/texture/public/360-viewer/v2') ||
    window.location.pathname.includes('/product/public/ai-video') ||
    window.location.pathname.includes('/product/public/ai-video/v2') ||
    window.location.pathname.includes('/texture/public/ai-video/v2') ||
    window.location.pathname.includes('/product/public/video') ||
    window.location.pathname.includes('/product/public/video/v2') ||
    window.location.pathname.includes('/texture/public/video/v2') ||
    window.location.pathname.includes('/product/public/panning-video') ||
    window.location.pathname.includes('/product/public/ar-viewer') ||
    window.location.pathname.includes('/product/public/ar-viewer/v2') ||
    window.location.pathname.includes('/configurator-public-viewer') ||
    window.location.pathname.includes('/configurator-public-viewer/v2') ||
    window.location.pathname.includes('/configurator-public-viewer/texture/v2')
  );
};

export const updateRenderPreviewData = async () => {
  const configuratorId = selectConfiguratorId(getAppState());
  if (configuratorId) {
    const result = await apiGetPreviewListing(`configurator=${configuratorId}`);
    let updatedResult = [];
    if (result?.results?.length) {
      // store updated data in redux
      dispatcher(configuratorActions.setRenderPreviewData([...result?.results]));
    }
    return updatedResult;
  }
};

export const checkIfDisabled = (scene) => {
  const renderPreviewData = selectRenderPreview(getAppState());
  const sceneId = scene || selectSelectedSceneId(getAppState());
  if (renderPreviewData?.length) {
    const checkIfScenePresent = renderPreviewData.find(
      (data) => parseInt(data?.scene) === parseInt(sceneId)
    );
    return Boolean(checkIfScenePresent);
  }
  return false;
};

export const getAspectRatioData = async () => {
  const configuratorData = selectConfiguratorData(getAppState());
  let isUpdate = false;
  const keysToCheck = [
    'aspect_ratio',
    'high_reso_x',
    'high_reso_y',
    'low_reso_x',
    'low_reso_y',
    'compressed_reso_x',
    'compressed_reso_y',
    'bg_reso_x',
    'bg_reso_y',
  ];
  for (let i = 0; i < keysToCheck.length; i++) {
    if (!configuratorData[keysToCheck[i]]) {
      isUpdate = true;
      break;
    }
  }
  const aspectRatios = selectAllAspectRatio(getAppState());
  let ratioOptions = [];
  if (aspectRatios?.length) {
    ratioOptions = [...aspectRatios];
  } else {
    const response = await apiGetAllAspectRatio();
    if (response?.results?.length) {
      ratioOptions = [...response?.results];
      dispatcher(configuratorActions.setAllAspectRatio(ratioOptions));
    }
  }
  const selectedAspectRatio = ratioOptions.find((op) => op.aspect_ratio === ASPECT_RATIO['1.33']);
  const data = {
    aspect_ratio: configuratorData?.aspect_ratio || selectedAspectRatio?.aspect_ratio,
    high_reso_x: configuratorData?.high_reso_x || selectedAspectRatio?.high_reso_x,
    high_reso_y: configuratorData?.high_reso_y || selectedAspectRatio?.high_reso_y,
    low_reso_x: configuratorData?.low_reso_x || selectedAspectRatio?.low_reso_x,
    low_reso_y: configuratorData?.low_reso_y || selectedAspectRatio?.low_reso_y,
    compressed_reso_x:
      configuratorData?.compressed_reso_x || selectedAspectRatio?.compressed_reso_x,
    compressed_reso_y:
      configuratorData?.compressed_reso_y || selectedAspectRatio?.compressed_reso_y,
    bg_reso_x: configuratorData?.high_reso_x || selectedAspectRatio?.high_reso_x,
    bg_reso_y: configuratorData?.high_reso_y || selectedAspectRatio?.high_reso_y,
    preview_reso_x: configuratorData?.low_reso_x || selectedAspectRatio?.low_reso_x,
    preview_reso_y: configuratorData?.low_reso_y || selectedAspectRatio?.low_reso_y,
    render_reso_x: configuratorData?.high_reso_x || selectedAspectRatio?.high_reso_x,
    render_reso_y: configuratorData?.high_reso_y || selectedAspectRatio?.high_reso_y,
  };
  if (isUpdate) {
    updateConfiguratorData({ payload: data });
  }
  return data;
};

export const isUserLoggedInConfigurator = () => {
  const pathname = window.location.pathname;
  const splitStr = pathname.split('/');
  const isPublicLink = splitStr[splitStr?.length - 1]?.includes('-');
  if (isPublicLink) {
    return false;
  }
  if (getLocalStorage(REFRESH_TOKEN) && getLocalStorage(AUTH_TOKEN)) {
    return true;
  }
  return false;
};

export const isAbleToChangeOrder = () => {
  const renderPreviewData = selectRenderPreview(getAppState());
  const sceneId = selectSelectedSceneId(getAppState());
  const inCompleteData = renderPreviewData?.filter(
    (val) => parseInt(sceneId) === parseInt(val.scene) && !getStatus(val.status)
  );
  if (inCompleteData?.length) {
    return false;
  }
  return true;
};

export const getStatus = (status) => {
  status = parseInt(status);
  if (status === 1 || status === 3) {
    return true;
  }
  return false;
};

export const getSceneLabelOptions = async ({
  token,
  v2 = false,
  sceneId,
  storeId,
  extraParams = {},
}) => {
  let labels = await getLabelsDataInPrototype({
    token,
    sceneId,
    storeId,
    v2,
    isPublic: !isUserLoggedInConfigurator(),
  });
  let allLabels = labels;
  if (isPermissionToUpdateRenders() && isUserLoggedInConfigurator()) {
    const baseLabel = labels?.filter((val) => val?.is_base);
    const otherLabels = labels?.filter((val) => !val?.is_base);
    allLabels = [...baseLabel, ...otherLabels];
  } else {
    allLabels = labels?.filter((val) => !val?.is_base);
  }
  await Promise.all(
    allLabels.map(async (val) => {
      const textures = await dispatcher(
        getOptionsDataInPrototype({
          token,
          optionId: val.id,
          storeId,
          v2,
          isPublic: !isUserLoggedInConfigurator(),
          sceneId,
          extraParams,
        })
      );
      val.textures = textures.results;
      return val;
    })
  );
  return allLabels;
};

export const isPermissionToUpdateRenders = () => {
  const currentUser = getLocalStorage(CURRENT_USER, true);
  const permissions = currentUser?.permissions_list || [];
  const isPresent = permissions.find(
    (val) => val.codename === PERMISSIONS.UPDATE_CONFIGURATOR_RENDERS.codename
  );
  return Boolean(isPresent);
};

export const checkIfDefaultMaterialIsPresentInLabel = async ({
  labelId,
  returnOptions = false,
}) => {
  const sceneId = selectSelectedSceneId(getAppState());
  const response = await getDefaultTextures({ labelId, sceneId });
  if (response?.length) {
    return returnOptions ? response : true;
  }
  return returnOptions ? [] : false;
};

export const base64Resize = ({
  sourceBase64,
  mimeType = 'image/jpeg',
  height = 500,
  width = 500,
  callback,
}) => {
  const img = document.createElement('img');
  img.src = sourceBase64;
  img.onload = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(img, 0, 0, width, height);
    const newBase64 = canvas.toDataURL(mimeType);
    callback && callback(newBase64);
  };
};

const popDefaultTextureData = () => {
  const allDefaultData = selectDefaultTextures(getAppState());
  const copyData = JSON.parse(JSON.stringify(allDefaultData));
  if (copyData && copyData.length) {
    copyData.pop();
    dispatcher(configuratorActions.setDefaultTextures(copyData));
  }
};

export const storeDefaultObjectMaterialImage = ({ imageData, textureName }) => {
  const objectsWithDefaultThumbnail = selectObjectsWithDefaultThumbnail(getAppState());
  const isPresent = objectsWithDefaultThumbnail.find((val) => val.textureName === textureName);
  if (!isPresent) {
    const thumb = imageData ? `data:image/jpeg;base64,${imageData}` : null;
    if (thumb) {
      base64Resize({
        sourceBase64: thumb,
        callback: (newBase64) => {
          const image = newBase64.split(',');
          const blobImage = base64ToBlob({ imageString: image[1] });
          dispatcher(
            configuratorActions.setObjectsWithDefaultThumbnail({
              textureName,
              blobImage,
              thumbnail: newBase64,
            })
          );
          popDefaultTextureData();
        },
      });
    } else {
      dispatcher(
        configuratorActions.setObjectsWithDefaultThumbnail({
          textureName,
          blobImage: null,
          thumbnail: null,
        })
      );
      popDefaultTextureData();
    }
  } else {
    popDefaultTextureData();
  }
};

export const getLabelValueData = (data, valueKey = 'name', labelKey = 'name') => {
  if (!data.length) return [];
  return data.map((a) => {
    return {
      ...a,
      value: a[valueKey],
      label: a[labelKey],
    };
  });
};

export const uploadDefaultTextureThumbnail = async ({ blobImage, fileName, presigned_data }) => {
  try {
    await uploadDefaultThumbnail({
      blobImage,
      presigned_data,
      fileName,
    });
  } catch (error) {
    errorToastV2(`There was an error while uploading texture file: ${fileName}. Please try again`);
  }
};

export const updateThumbnailDefaultObjects = async ({ defaultObjects, labelId }) => {
  const objectsWithDefaultThumbnail = selectObjectsWithDefaultThumbnail(getAppState());
  const finalOptions = [];
  defaultObjects?.map((val) => {
    const object = {
      ...val,
      texture_type: !val?.color_code ? OPTION_TYPE.MATERIAL : OPTION_TYPE.COLOR,
      thumb: !val?.color_code ? `${val?.display_name}.jpeg` : null,
    };
    delete object?.id;
    delete object?.thumbnail;
    delete object?.name;
    finalOptions.push(object);
  });
  const data = await dispatcher(addDefaultMaterialToLabelApi({ labelId, options: finalOptions }));
  const materials = defaultObjects?.filter(
    (val) => val?.texture_type === OPTION_TYPE.MATERIAL && !val?.color_code
  );
  if (data && materials?.length) {
    for (let i = 0; i < materials.length; i++) {
      const material = materials[i];
      const selectedMaterial = data.find((val) => val?.display_name === material?.display_name);
      const object = material?.material_properties || {};
      const textures = [
        object?.diffuseName,
        object?.normalMapName,
        object?.aoMapName,
        object?.roughnessMapName,
        object?.metallicMapName,
        object?.transparencyMapName,
      ].filter(Boolean);
      const objectWithThumbnail = objectsWithDefaultThumbnail?.find(
        (ins) => (textures?.length && textures[0]) === ins.textureName
      );
      const blobImage = objectWithThumbnail?.blobImage;
      if (!blobImage) {
        errorToastV2('Default thumbnail is not present');
      }
      if (blobImage) {
        await uploadDefaultTextureThumbnail({
          blobImage,
          fileName: material?.display_name,
          presigned_data: selectedMaterial?.presigned_data_thumbnail,
        });
      }
    }
    await dispatcher(getLabelOptions({ labelId, onlyOnce: false }));
  }
};

export const getDefaultTextureData = ({ name }) => {
  const defaultTextures = window.defaultTexures;
  const allDefaultData = Object.values(defaultTextures);
  const data = allDefaultData.find((ins) => ins?.textureName === name);
  return data?.imageUrl || '';
};

export const updateTextureNameFromUrl = ({ url }) => {
  const defaultTextures = window.defaultTexures;
  const allDefaultData = Object.values(defaultTextures);
  const data = allDefaultData.find((ins) => ins?.imageUrl === url);
  return data?.materialId ? '' : data?.textureName || '';
};

export const addDefaultTextureName = (obj) => {
  if (obj?.diffusePath && !obj?.diffuseId) {
    obj.diffuseId = updateTextureNameFromUrl({ url: obj?.diffusePath });
  }
  if (obj?.normalPath && !obj?.normalId) {
    obj.normalId = updateTextureNameFromUrl({ url: obj?.normalPath });
  }
  if (obj?.roughnessPath && !obj?.roughnessId) {
    obj.roughnessId = updateTextureNameFromUrl({ url: obj?.roughnessPath });
  }
  if (obj?.transparencyPath && !obj?.transparencyId) {
    obj.transparencyId = updateTextureNameFromUrl({ url: obj?.transparencyPath });
  }
  if (obj?.metallicPath && !obj?.metallicId) {
    obj.metallicId = updateTextureNameFromUrl({ url: obj?.metallicPath });
  }
  if (obj?.AOPath && !obj?.AOId) {
    obj.AOId = updateTextureNameFromUrl({ url: obj?.AOPath });
  }
  return obj;
};

export const updateDefaultTextureData = ({ data }) => {
  const updatedData = [];
  const copyData = JSON.parse(JSON.stringify(data));
  copyData.map((val) => {
    const updatedObj = resetPathIfIdIsNotPresent(val);
    if (updatedObj.diffuseName) {
      updatedObj.diffusePath = getDefaultTextureData({ name: updatedObj.diffuseName });
    }
    if (updatedObj.normalMapName) {
      updatedObj.normalPath = getDefaultTextureData({ name: updatedObj.normalMapName });
    }
    if (updatedObj.aoMapName) {
      updatedObj.AOPath = getDefaultTextureData({ name: updatedObj.aoMapName });
    }
    if (updatedObj.roughnessMapName) {
      updatedObj.roughnessPath = getDefaultTextureData({ name: updatedObj.roughnessMapName });
    }
    if (updatedObj.metallicMapName) {
      updatedObj.metallicPath = getDefaultTextureData({ name: updatedObj.metallicMapName });
    }
    if (updatedObj.transparencyMapName) {
      updatedObj.transparencyPath = getDefaultTextureData({ name: updatedObj.transparencyMapName });
    }
    if (updatedObj?.changedMapsPath) {
      const defaultUpdatedPath = addDefaultTextureName(updatedObj?.changedMapsPath);
      const changedMapsPath = resetPathIfIdIsNotPresent(defaultUpdatedPath);
      updatedObj.changedMapsPath = changedMapsPath;
    }
    updatedData.push(updatedObj);
  });
  return updatedData;
};

export const resetPathIfIdIsNotPresent = (obj) => {
  if (!obj?.diffuseId) {
    obj.diffusePath = '';
  }
  if (!obj?.normalId) {
    obj.normalPath = '';
  }
  if (!obj?.roughnessId) {
    obj.roughnessPath = '';
  }
  if (!obj?.transparencyId) {
    obj.transparencyPath = '';
  }
  if (!obj?.metallicId) {
    obj.metallicPath = '';
  }
  if (!obj?.AOId) {
    obj.AOPath = '';
  }
  return obj;
};

export const removeDefaultTextures = async ({ parentObjects }) => {
  const labels = selectBackupLabels(getAppState());
  const sceneId = selectSelectedSceneId(getAppState());
  for (let i = 0; i < labels.length; i++) {
    const labelId = labels[i].id;
    const defaultOptions = await getDefaultTextures({ labelId, sceneId });
    const linkedObjects = labels[i].data.linkedObjects;
    const allObjectNames = linkedObjects.map((val) => val?.objectName);
    const defaultOptionIds = defaultOptions?.map((ins) => ins?.id);
    const allChildObjectNames = parentObjects?.reduce((acc, curr) => {
      acc = acc.concat(
        curr?.childObjectsName?.length ? curr?.childObjectsName : [curr?.objectName]
      );
      if (curr?.clickedObjectName) {
        acc.push(curr?.clickedObjectName);
      }
      return acc;
    }, []);
    const isObjectPresent = allObjectNames.find((val) => allChildObjectNames.indexOf(val) !== -1);
    if (defaultOptionIds?.length && Boolean(isObjectPresent)) {
      await dispatcher(
        removeOptionsFromLabelOptimistic({ labelId, ids: defaultOptionIds, showMessage: false })
      );
    }
  }
};

export const getInitialPropertiesIfDefaultIsPresent = ({ options }) => {
  const initialObjectProperties = selectInitialObjectProperties(getAppState());
  const allObjectNames = initialObjectProperties.map((val) => val?.realObjName || '');
  const missingObjects = [];
  options.map((val) => {
    if (allObjectNames.indexOf(val.display_name) === -1) {
      missingObjects.push(
        val?.material_properties?.blenderObjectName || val?.material_properties?.objectName
      );
    }
  });
  if (missingObjects.length) {
    LabelsController.AddRenderersToGroup('DEFAULT', missingObjects);
  }
};

export const sendDataToAddRenderers = ({ objectNames }) => {
  const initialObjectProperties = selectInitialObjectProperties(getAppState());
  const data = objectNames.map((val) => {
    const isPresent = initialObjectProperties?.find(
      (ins) => (ins?.blenderObjectName || ins?.objectName) === val
    );
    return {
      name: val,
      status: isPresent ? false : true,
    };
  });
  // const isSentToUnity = data?.some((val) => val.status);
  // if (isSentToUnity) {
  const addRendersCount = selectAddRenderersCount(getAppState());
  const count = addRendersCount + 1;
  dispatcher(configuratorActions.setAddRenderersCount(count));
  // }
  return data;
};

export const resetSelectedTexture = async ({ labelId }) => {
  const options = await getSelectedTextures({ labelId });
  const option = options.find((val) => val?.data?.is_selected || val?.is_selected);
  if (option) {
    const updateData = {
      id: option.id,
      is_selected: false,
    };
    await dispatcher(
      updateOptionApi({
        labelId,
        optionDataId: option?.data?.id,
        option: updateData,
      })
    );
  }
};

export const getSelectedOptionWithUpdatedThumbnail = ({
  option,
  selectedPreview,
  thumbnailUpdatedOptions,
}) => {
  let optionToSend = {};
  const selectedThumbnailOption = thumbnailUpdatedOptions.find(
    (val) => val.scene_texture === option.id
  );
  if (selectedPreview?.scene_texture === option?.id) {
    const selectedPreviewThumbnail = thumbnailUpdatedOptions.find(
      (val) => val?.renders === selectedPreview?.renders
    );
    optionToSend = {
      ...option,
      thumb: selectedPreviewThumbnail?.thumbnail || selectedPreview?.thumbnail || option?.thumb,
    };
  } else if (selectedThumbnailOption?.thumbnail) {
    optionToSend = {
      ...option,
      thumb: selectedThumbnailOption.thumbnail,
    };
  } else {
    optionToSend = option;
  }
  return optionToSend;
};

export const isV2 = () => {
  const version = selectConfiguratorVersion(getAppState());
  const isV2 = version === 'V2';
  return isV2;
};

export const getThreeSixtyFromViews = () => {
  const selectedView = getSelectedViewFromData();
  return Boolean(selectedView?.config_type === CONFIGURATOR_TYPE.THREE_SIXTY);
};

export const canDelete = () => {
  const linkedObjects = selectAllLinkedObjects(getAppState());
  const selectedObjectList = selectSelectedObjects(getAppState());
  const allObjectNames = linkedObjects.map((val) => val?.objectName);
  const allChildObjectNames = selectedObjectList?.reduce((acc, curr) => {
    acc = acc.concat(curr?.childObjectsName?.length ? curr?.childObjectsName : [curr?.objectName]);
    if (curr?.clickedObjectName) {
      acc.push(curr?.clickedObjectName);
    }
    return acc;
  }, []);
  const isObjectPresent = allObjectNames.find((val) => allChildObjectNames.indexOf(val) !== -1);
  const tryingToDeleteLinkedObject = Boolean(isObjectPresent);
  if (tryingToDeleteLinkedObject) {
    return false;
  }
  return true;
};

export const applyToLabelAfterLinking = async ({ labelId }) => {
  const options = selectSortedLabelOptions(labelId)(getAppState());
  const selectedOptions = await getSelectedTextures({ labelId });
  const isSelected = selectedOptions.find((val) => val?.data?.is_selected || val?.is_selected);
  const defaultOptions = await checkIfDefaultMaterialIsPresentInLabel({
    labelId,
    returnOptions: true,
  });
  if (options?.length && defaultOptions?.length && !isSelected) {
    const option = defaultOptions?.find((val) => val?.data?.is_default || val?.is_default);
    forceApplyOption({ option });
  }
};

export const checkIfObjectIsPresent = ({ labelId, draggedIds, movedDraggedIds }) => {
  let isPresent = false;
  const initialObjectProperties = selectInitialObjectProperties(getAppState());
  const filteredData = initialObjectProperties.filter((ins) => {
    const objectName = ins?.blenderObjectName || ins?.objectName;
    return draggedIds.indexOf(objectName) !== -1;
  });
  if (draggedIds.length === filteredData.length) {
    isPresent = true;
  } else {
    isPresent = false;
  }
  dispatcher(configuratorActions.setAllDataProcessed(isPresent));
  dispatcher(configuratorActions.setDefaultMaterialData({ draggedIds: movedDraggedIds, labelId }));
};

export const getUpdatedLinkedObjectsToSend = ({
  updatedLabels,
  updateObj = {
    linkedObjects: true,
    seq_no: false,
  },
}) => {
  const data = updatedLabels.map((val) => {
    const obj = {
      id: val?.id,
      data: {},
    };
    if (isV2()) {
      obj.configurator = selectConfiguratorId(getAppState());
      obj.data.id = val?.data?.id;
      obj.data.scene = val?.data?.scene;
    }
    if (updateObj.linkedObjects) {
      obj.data.linkedObjects = val?.data?.linkedObjects || [];
    }
    if (updateObj.seq_no) {
      if (isV2()) {
        obj.data.seq_no = val?.data?.seq_no;
      } else {
        obj.seq_no = val?.seq_no;
      }
    }
    if (isEmpty(obj.data)) {
      delete obj?.data;
    }
    if (isV2() && obj?.data) {
      obj.data = [obj.data];
    }
    return obj;
  });
  return data;
};

export const updateTransformationInView = debounce(async ({ transformChangedObjectData } = {}) => {
  const unitySceneLoaded = selectUnitySceneLoaded(getAppState());
  const isAddView = selectIsAddViewRoute(getAppState());
  if (isAddView || !unitySceneLoaded) {
    return;
  }
  const sceneId = selectSelectedSceneId(getAppState());
  const view = getSelectedViewFromData();
  const transformationData = await getTransformationData();
  const isUpdateAble = !view?.is_render && !view?.is_preview;
  if (!isUpdateAble || !view?.id) {
    return;
  }
  dispatcher(
    updateViewTransformationOptimistic({
      sceneId: sceneId,
      view: {
        id: view.id,
        extra_data: {
          transformChangedObjectData: !isEmpty(transformChangedObjectData)
            ? transformChangedObjectData
            : transformationData,
        },
      },
    })
  );
}, 500);

export const getTransformationData = async () => {
  const unitySceneLoaded = selectUnitySceneLoaded(getAppState());
  if (!unitySceneLoaded) {
    return;
  }
  const sceneJson = await waitForEventOnce(CONFIGURATOR_UNITY_EVENT.GetRoomInfo, () =>
    configuratorModule.GetConfiguratorJson()
  );
  return sceneJson?.transformChangedObjectData || {};
};

export const loadTransformationDataInUnity = async ({ view } = {}) => {
  const unitySceneLoaded = selectUnitySceneLoaded(getAppState());
  if (!unitySceneLoaded) {
    return;
  }
  let transformChangedObjectData = view?.extra_data?.transformChangedObjectData || {};
  if (isEmpty(transformChangedObjectData)) {
    transformChangedObjectData = await getTransformationData();
  }
  const data = {
    transformChangedObjects: transformChangedObjectData,
  };
  if (!isEmpty(transformChangedObjectData)) {
    configuratorBridge.MainController.UpdateConfiguratorPropsInfoReceiver(data);
  }
};

export const updateTransformInViewsOnDelete = async ({ objects }) => {
  const sceneId = selectSelectedSceneId(getAppState());
  const allChildObjectNames = objects?.reduce((acc, curr) => {
    acc = acc.concat(curr?.childObjectsName?.length ? curr?.childObjectsName : [curr?.objectName]);
    if (curr?.clickedObjectName) {
      acc.push(curr?.clickedObjectName);
    }
    if (curr.isParent) {
      acc.push(curr.objectName);
    }
    return acc;
  }, []);
  const views = selectCurrentSceneViews(getAppState());
  const filteredViews = views.filter((view) => !view?.is_render && !view?.is_preview);
  if (!filteredViews?.length) {
    return;
  }
  await Promise.all(
    filteredViews.map(async (val) => {
      const transformData = JSON.parse(
        JSON.stringify(val?.extra_data?.transformChangedObjectData || {})
      );
      if (!isEmpty(transformData)) {
        const objectKeys = Object.keys(transformData);
        objectKeys.map((key) => {
          if (allChildObjectNames.indexOf(key) !== -1) {
            delete transformData[key];
          }
        });
        await dispatcher(
          updateViewTransformationOptimistic({
            sceneId: sceneId,
            view: {
              id: val.id,
              extra_data: {
                transformChangedObjectData: transformData,
              },
            },
          })
        );
      }
    })
  );
};

export const updateTransformInViewsOnAddProduct = debounce(async () => {
  const sceneId = selectSelectedSceneId(getAppState());
  const currentTransformData = await getTransformationData();
  const currentTransformDataObjectNames = Object.keys(currentTransformData);
  const views = selectCurrentSceneViews(getAppState());
  const filteredViews = views.filter((view) => !view?.is_render && !view?.is_preview);
  if (!filteredViews?.length) {
    return;
  }
  await Promise.all(
    filteredViews.map(async (val) => {
      const transformData = JSON.parse(
        JSON.stringify(val?.extra_data?.transformChangedObjectData || {})
      );
      if (!isEmpty(transformData)) {
        currentTransformDataObjectNames.map((key) => {
          if (!transformData[key]) {
            transformData[key] = currentTransformData[key];
          }
        });
        await dispatcher(
          updateViewTransformationOptimistic({
            sceneId: sceneId,
            view: {
              id: val.id,
              extra_data: {
                transformChangedObjectData: transformData,
              },
            },
          })
        );
      }
    })
  );
}, 500);

export const getSelectedViewFromData = () => {
  const sceneId = selectSelectedSceneId(getAppState());
  let viewDetails = selectSelectedViewInScene(sceneId)(getAppState());
  if (isEmpty(viewDetails)) {
    viewDetails = selectSelectedView(getAppState());
  }
  const views = selectCurrentSceneViews(getAppState());
  let view = {};
  if (viewDetails?.id) {
    view = viewDetails;
  } else {
    view = views?.[0] || {};
  }
  return view;
};

export const isConfiguratorPropSelected = () => {
  const isSelected = selectIsConfiguratorPropSelected(getAppState());
  return isSelected;
};

export const isAlignDialogOpen = () => {
  const isSelected = selectOpenAlignmentTool(getAppState());
  const refrenceObjectPopup = selectShowInfoWarningPopup(getAppState());
  return isSelected || refrenceObjectPopup;
};

export const getObjectNameFromUnity = async (id) => {
  const sceneJson = await waitForEventOnce(CONFIGURATOR_UNITY_EVENT.GetRoomInfo, () =>
    configuratorModule.GetConfiguratorJson()
  );
  const copyData = JSON.parse(JSON.stringify(sceneJson));
  const sceneProductData = copyData?.transformChangedObjectData || {};
  const deletedData = copyData?.deletedObjectData || copyData?.deletedObjectsData || [];
  const firstMatchedKey = Object.keys(sceneProductData).find((key) => {
    const isKeyPresent = deletedData.some((ins) => key.includes(ins));
    return key.includes(id) && !isKeyPresent;
  });
  return sceneProductData[firstMatchedKey].objectName;
};

export const showLabelInPrototype = ({ options, label, v2 }) => {
  const isVisible = v2 ? Boolean(label?.data?.is_visible) : true;
  if (
    isVisible &&
    isUserLoggedInConfigurator() &&
    (isPermissionToUpdateRenders() || (!isPermissionToUpdateRenders() && options?.length > 1))
  ) {
    return true;
  }
  if (isVisible && !isUserLoggedInConfigurator() && options?.length > 1) {
    return true;
  }
  return false;
};

export const getUrlExtension = (url) => {
  return url.split(/[#?]/)[0].split('.').pop().trim();
};

export const sanitizeNameInConfigurator = (value, { maxLength, warningMessage = '' } = {}) => {
  if (!typeof value === 'string') value;
  const regex = new RegExp(NAME_EXCLUDED_SPECIAL_CHARS_V2_GLOBAL, 'g');
  value = value.replace(regex, '');
  if (value.length > maxLength) {
    value = value.substring(0, maxLength);
    if (warningMessage) warningToast(warningMessage);
  }
  return value;
};

export const enableBackgroundInViewsAfterUpdate = (views = []) => {
  const updatedViews = selectCurrentSceneViews(getAppState());
  const data = views?.map((view) => {
    const matchingOldView = updatedViews.find((data) => data?.id === view?.id);
    if (matchingOldView?.id && view?.latest_bg_detail?.id) {
      view.latest_bg_detail.load_in_viewport =
        matchingOldView?.latest_bg_detail?.load_in_viewport || false;
    }
    return view;
  });
  return data;
};

export const setStartEndAngleInUnity = ({ view }) => {
  const unitySceneLoaded = selectUnitySceneLoaded(getAppState());
  if (
    isEmpty(view?.camera_data) ||
    !isV2() ||
    !unitySceneLoaded ||
    view?.config_type !== CONFIGURATOR_TYPE.THREE_SIXTY
  ) {
    return;
  }
  const camera = view?.camera_data;
  configuratorBridge.UpdateCameraProperties({
    id: camera?.id || uuidv4(),
    propertyName: 'cameraStartAngle',
    value: view?.start_angle || DEFAULT_START_ANGLE,
  });
  configuratorBridge.UpdateCameraProperties({
    id: camera?.id || uuidv4(),
    propertyName: 'cameraLastAngle',
    value: view?.last_angle || DEFAULT_END_ANGLE,
  });
};

export const updateConfiguratorDataRealTime = async ({ data }) => {
  const { scene_id, configurator_id } = data;
  const sceneId = selectSelectedSceneId(getAppState());
  const configuratorId = selectConfiguratorId(getAppState());
  if (parseInt(configurator_id) !== parseInt(configuratorId)) {
    return;
  }
  if (data?.scene_duplicate) {
    await dispatcher(getConfiguratorScenes(configuratorId, false));
    if (parseInt(sceneId) === parseInt(scene_id)) {
      init(sceneId);
    }
  }
  if (data?.is_preview || data?.is_render) {
    const payload = { configuratorId };
    dispatcher(getConfiguratorApi(payload, false));
    dispatcher(getConfiguratorScenes(configuratorId, false));
    dispatcher(getSceneViewsApi({ sceneId, showLoading: false, onlyOnce: false }));
    if (parseInt(sceneId) === parseInt(scene_id)) {
      const labels = await loadSceneLabels({ sceneId, onlyOnce: false, onlyRedux: true });
      labels.map((label) => {
        dispatcher(getLabelOptions({ labelId: label.id, onlyOnce: false, onlyRedux: true }));
      });
    }
  }
  if (data?.sceneoption_duplicate && parseInt(sceneId) === parseInt(scene_id)) {
    dispatcher(
      getLabelOptions({ labelId: data?.sceneoption_id, onlyOnce: false, onlyRedux: true })
    );
  }
};

export const isGlassMaterial = ({ material = {} }) => {
  return material?.filteroptions?.some((val) => (val?.id ? val?.id : val) === GLASS_MATERIAL_ID);
};

export const applyDefaultMaterialOnLabel = ({
  option,
  labelId,
  objects,
  applySameObjectMaterialAfterLinking,
}) => {
  if (option) {
    const updateData = {
      id: option?.id,
      is_selected: true,
    };
    dispatcher(
      updateOptionApi({
        labelId,
        optionDataId: option?.data?.id,
        option: updateData,
      })
    );
  }
  applySameObjectMaterialAfterLinking({ allObjects: objects, applyFromObject: objects[0] });
};

export const isViewsRouteOpen = () => {
  const isViewsRoute = selectIsViewsRoute(getAppState());
  const isViewSettingsRoute = selectIsViewSettingsRoute(getAppState());
  const isViewsOpen = isViewsRoute || isViewSettingsRoute;
  return Boolean(isViewsOpen);
};

export const getUrlOrIframeUrlWithToken = ({ token, isIframe = false }) => {
  const url = window.location.origin + '/configurator-viewer/' + token;
  if (isIframe) {
    return `<iframe src="${url}"></iframe>`;
  }
  return url;
};

export const isRetailAutomation = () => {
  return window.location.pathname.includes(RETAIL_AUTOMATION_ROUTE_V2); // retail-automation
};
export const isRetailConfigurator = () => {
  return window.location.pathname.includes(RETAIL_CONFIGURATOR_VIEWER_ROUTE); // retail-configurator-viewer
};

export const convertToKeyValueIframe = (data = {}) => {
  const obj = {
    key: '',
    value: '',
    style: '',
  };
  const mainKeyMappings = [
    'key',
    'Key',
    'type',
    'Type',
    'scene',
    'Scene',
    'label',
    'Label',
    'action',
    'Action',
  ];
  const otherKeyMappings = ['imageDownload', 'ImageDownload', 'Imagedownload', 'imagedownload'];
  const keyMappings = [...mainKeyMappings, ...otherKeyMappings];
  for (const key of keyMappings) {
    if (data[key]) {
      obj.key = key.toLowerCase() === 'scene' ? 'scene' : data[key];
      if (key.toLowerCase() === 'scene') obj.value = data[key];
      if (otherKeyMappings.includes(key)) obj.key = 'imageDownload';
      break;
    }
  }
  const valueMappings = [
    'value',
    'Value',
    'val',
    'Val',
    'texture',
    'Texture',
    'element',
    'Element',
    'selector',
    'Selector',
    'currentImage',
    'CurrentImage',
    'Currentimage',
    'currentimage',
  ];
  for (const key of valueMappings) {
    if (data[key]) {
      obj.value = key.toLowerCase() === 'currentimage' ? 'currentImage' : data[key];
      break;
    }
  }
  const styleMappings = ['style', 'Style'];
  for (const key of styleMappings) {
    if (data[key]) {
      obj.style = data[key];
      break;
    }
  }
  return obj;
};

export const getParentDomain = () => {
  if (window.document.referrer) return window.document.referrer;
  if (window.location.ancestorOrigins && window.location.ancestorOrigins.length > 0) {
    return window.location.ancestorOrigins[0]; // parent domain
  }
  return null; // fallback
};

export const enablePostHog = () => {
  const parentDomain = getParentDomain();
  if (!parentDomain) return false;
  try {
    const url = new URL(parentDomain);
    return (
      url.hostname === LEISURE_CREATIONS_DOMAIN ||
      // url.hostname === TEST_DOMAIN || // TO DO: This is only for testing purpose
      url.hostname === WESTMINSTER_DOMAIN
    );
  } catch {
    return false;
  }
};

export const getProductNameAndIds = async (sceneId) => {
  const results = await getPublicProductData(sceneId);
  let productNames = [];
  let productIds = [];
  let productOrgIds = [];
  let productOrgNames = [];
  let orgName = '';
  let orgId = '';
  let rawData = [];
  if (results?.length) {
    results.map((val) => {
      if (val?.product_name) {
        productNames.push(val?.product_name);
      }
      if (val?.product_id) {
        productIds.push(val?.product_id);
      }
      if (val?.product_owner?.id) {
        productOrgIds.push(val?.product_owner?.id);
      }
      if (val?.product_owner?.name) {
        productOrgNames.push(val?.product_owner?.name);
      }
    });
    productNames = uniq(productNames);
    productIds = uniq(productIds);
    productOrgNames = uniq(productOrgNames);
    productOrgIds = uniq(productOrgIds);
    orgName = results[0]?.configurator_org_details?.name || '';
    orgId = results[0]?.configurator_org_details?.id || '';
    rawData = results;
  }
  return {
    productIds,
    productNames,
    productOrgIds,
    productOrgNames,
    orgName,
    orgId,
    rawData,
  };
};
